import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "../firebase";

const CONFIG_CACHE_KEY = "wiblo_legal_config";
const CONFIG_CACHE_DURATION = 60 * 60 * 1000;

const DOCUMENT_CACHE_DURATION = 60 * 60 * 1000;

function getCachedValue(key, duration) {
  try {
    const cachedValue = localStorage.getItem(key);

    if (!cachedValue) {
      return null;
    }

    const parsedValue = JSON.parse(cachedValue);

    if (
      !parsedValue.savedAt ||
      Date.now() - parsedValue.savedAt > duration
    ) {
      localStorage.removeItem(key);
      return null;
    }

    return parsedValue.value;
  } catch (error) {
    console.warn("No fue posible leer la caché:", error);
    return null;
  }
}

function saveCachedValue(key, value) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        value,
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn("No fue posible guardar la caché:", error);
  }
}

async function getLegalConfig() {
  const cachedConfig = getCachedValue(
    CONFIG_CACHE_KEY,
    CONFIG_CACHE_DURATION
  );

  if (cachedConfig) {
    return cachedConfig;
  }

  const configReference = doc(db, "config", "game");
  const configSnapshot = await getDoc(configReference);

  if (!configSnapshot.exists()) {
    throw new Error("No existe el documento config/game.");
  }

  const configData = configSnapshot.data();

  const legalConfig = {
    privacyPath: configData.privacyPath || "",
    termsPath: configData.termsPath || "",
  };

  saveCachedValue(CONFIG_CACHE_KEY, legalConfig);

  return legalConfig;
}

function LegalDocumentPage({
  documentType,
  title,
  description,
}) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadDocument() {
      try {
        setLoading(true);
        setErrorMessage("");

        const config = await getLegalConfig();

        const storagePath =
          documentType === "privacy"
            ? config.privacyPath
            : config.termsPath;

        if (!storagePath) {
          throw new Error(
            `No hay una ruta configurada para ${documentType}.`
          );
        }

        const documentCacheKey = `wiblo_legal_document_${storagePath}`;

        const cachedDocument = getCachedValue(
          documentCacheKey,
          DOCUMENT_CACHE_DURATION
        );

        if (cachedDocument) {
          if (isMounted) {
            setContent(cachedDocument);
          }

          return;
        }

        const storageReference = ref(storage, storagePath);
        const downloadUrl = await getDownloadURL(storageReference);

        const response = await fetch(downloadUrl);

        if (!response.ok) {
          throw new Error(
            `No fue posible descargar el documento: ${response.status}`
          );
        }

        const markdown = await response.text();

        saveCachedValue(documentCacheKey, markdown);

        if (isMounted) {
          setContent(markdown);
        }
      } catch (error) {
        console.error("Error cargando documento legal:", error);

        if (isMounted) {
          setErrorMessage(
            "No fue posible cargar este documento. Inténtalo nuevamente más tarde."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    document.documentElement.classList.add("legal-document-html");
    document.body.classList.add("legal-document-body");

    window.scrollTo({
      top: 0,
      behavior: "auto",
    });

    loadDocument();

    return () => {
      isMounted = false;

      document.documentElement.classList.remove(
        "legal-document-html"
      );

      document.body.classList.remove(
        "legal-document-body"
      );
    };
  }, [documentType]);

  return (
    <main className="legal-document-page">
      <section className="legal-document-container">
        <header className="legal-document-header">
          <div className="legal-document-logos">
            <img
              src="/images/LogotipoWiblocolor1.png"
              alt="Logo de Wiblo"
              className="legal-document-logo legal-document-logo--wiblo"
            />

            <span className="legal-document-logo-divider" />

            <img
              src="/images/LogoHumanSlogan.png"
              alt="Logo de Human Bionics"
              className="legal-document-logo legal-document-logo--human"
            />
          </div>

          <div className="legal-document-header-content">
            <span className="legal-document-eyebrow">
              Documentos legales
            </span>

            <h1>{title}</h1>

            {description && <p>{description}</p>}
          </div>
        </header>

        <div className="legal-document-content">
          {loading && (
            <div className="legal-document-status">
              <span className="legal-document-spinner" />
              <p>Cargando documento...</p>
            </div>
          )}

          {!loading && errorMessage && (
            <div className="legal-document-error" role="alert">
              <strong>No se pudo cargar el documento</strong>
              <p>{errorMessage}</p>
            </div>
          )}

          {!loading && !errorMessage && (
            <article className="legal-document-markdown">
              <ReactMarkdown>{content}</ReactMarkdown>
            </article>
          )}
        </div>

        <footer className="legal-document-footer">
          <p>
            Wiblo es una aplicación desarrollada por Human Bionics.
          </p>

          <span>
            © {new Date().getFullYear()} Human Bionics
          </span>
        </footer>
      </section>
    </main>
  );
}

export default LegalDocumentPage;